import { ALL_RISKS_PASSED_LABEL } from "@sscs/report/constants";
import { SDLCMetadata } from "@sscs/types";
import {
  Community,
  CommunityPackageDetails,
  CommunityRiskObject,
  CommunityVersion,
  SUPPORTED_OSS_COMMUNITIES,
} from "Types";
import { decomposePurl } from "Utils";

// only mapping the purl types that aren't the same as a Spectra Community community
const PURL_TYPE_TO_COMMUNITY_MAP: Record<string, string> = {
  gem: "rubygems",
  vsx: "vscode",
};

export type getCommunityUrlProps = {
  community: string;
  name: string;
  namespace?: string;
  version?: string;
  artifact?: string;
};

export const getCommunityUrl = ({
  community,
  name,
  namespace,
  version,
  artifact,
}: getCommunityUrlProps): string => {
  return `https://secure.software/${PURL_TYPE_TO_COMMUNITY_MAP[community] ?? community}/packages/${
    namespace ? `${namespace}/` : ""
  }${name}${version ? `/${version}` : ""}${artifact ? `/${artifact}` : ""}`;
};

export const getDeploymentRisk = (
  risk: CommunityVersion["assessments"],
): {
  status: SDLCMetadata.assessment_status_t;
  label: string;
  category?: keyof CommunityRiskObject;
} => {
  const finalRisk = Object.entries(risk).find(([, value]) => value.final) as
    | [keyof CommunityRiskObject, SDLCMetadata.risk_assessment_t]
    | undefined;

  if (!finalRisk) {
    return { status: "pass", label: ALL_RISKS_PASSED_LABEL };
  }

  // Covers cases like "package removed" where there is no issue count
  const label =
    finalRisk[1].count !== 0
      ? `${finalRisk[1].count.toLocaleString()} ${finalRisk[1].label}`
      : finalRisk[1].label;

  return {
    status: finalRisk[1].status,
    label: label,
    category: finalRisk[0],
  };
};

export const isSupportedCommunity = (value: unknown): value is Community => {
  return SUPPORTED_OSS_COMMUNITIES.some((community) => community === value);
};

export const getCommunityPackageDetails = (
  purl: string,
): CommunityPackageDetails | null => {
  const decomposedPurl = decomposePurl(purl);

  if (!decomposedPurl) {
    return null; // invalid purl
  }

  return {
    ...decomposedPurl,
    community:
      PURL_TYPE_TO_COMMUNITY_MAP[decomposedPurl.type] ?? decomposedPurl.type,
  };
};

export function isSdlcRiskAssessment(
  risk: CommunityRiskObject,
): risk is SDLCMetadata.risk_object_t {
  return risk.repository === undefined;
}
